import React, { useState, useEffect } from "react";
import api, { apiErr, downloadFile, refusalSentence } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Btn, PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useStudentFromAddress } from "@/lib/studentFromAddress";
import { useFillOnOpen } from "@/lib/fillOnOpen";
import { useAttention, otherYearsWaiting, ALSO_WAITING_IN, alsoWaitingIn } from "@/lib/attentionLanding";
import { DecisionDialog } from "@/components/DecisionDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Folders, ShieldCheck, CheckCircle, Warning, ArrowSquareOut, XCircle, Spinner, PencilSimple } from "@phosphor-icons/react";
import { fmtDate, money } from "@/lib/format";
import { MoneyInput, typedFigure } from "@/lib/accountingFormat";
import { CurrencySelect } from "@/components/CurrencySelect";
import { curCode } from "@/lib/currencies";
import { TaxYearInput } from "@/components/TaxYearInput";
import { useSearchParams, Link } from "react-router-dom";
import { normalizeCountryCode } from "./Students";
import { NotesField } from "@/components/NotesField";

/** Determine the primary school's country info for a student object. */
function schoolCountryInfo(student) {
  const activeSchools = (student?.schools || []).filter(s => !s.archived);
  const primary = activeSchools[0];
  const country = normalizeCountryCode(primary?.country || "US");
  return { country, isCanada: country === "CA", isForeign: country !== "US" };
}

/** Return the tuition-form label for the student's school country. */
function tuitionFormLabel(student) {
  const { isCanada, isForeign } = schoolCountryInfo(student);
  if (isCanada) return "T2202";
  if (isForeign) return "Tuition";
  return "1098-T";
}

export default function TaxForms() {
  const { user, selectedStudentId } = useAuth();
  const isOwner = user?.role === "owner";
  const qc = useQueryClient();
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const [tyiDlg, setTyiDlg] = useState(false);
  const [tyiDefaultStudent, setTyiDefaultStudent] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStudents = students.filter((s) => !s.archived);
  const tyiRefresh = () => { qc.invalidateQueries({ queryKey: ["tax-year-inputs"] }); qc.invalidateQueries({ queryKey: ["dashboard-tax-year"] }); };

  // ── Single page-level tax year (default: prior year = usual filing target) ──
  // A "Review now" link names the year its items are in and the beneficiary;
  // the picker moves to them and the address is cleared. (ZEN-768)
  const [pageTaxYear, setPageTaxYear] = useState(() => {
    const linked = searchParams.get("tax_year");
    return /^\d{4}$/.test(linked || "") ? Number(linked) : new Date().getFullYear() - 1;
  });
  useStudentFromAddress({ alsoClear: ["tax_year"] });

  // Deep-link: ?open=reserve → redirect to beneficiary card on Beneficiary(ies) page
  useEffect(() => {
    const openParam = searchParams.get("open");
    if (openParam === "reserve" && students.length > 0) {
      const beneficiary = searchParams.get("beneficiary") || selectedStudentId || "";
      setSearchParams({}, { replace: true });
      // Navigate to Beneficiary(ies) page with scroll to AOTC control
      const linkYear = searchParams.get("taxYear");
      window.location.href = `/students?aotc=${beneficiary}${/^\d{4}$/.test(linkYear || "") ? `&year=${linkYear}` : ""}`;
    }
  }, [searchParams, students.length]); // eslint-disable-line

  return (
    <div>
      <PageHeader title="Tax Forms & Audit" subtitle="Enter your 1099-Q, 1098-T, and AOTC details by tax year, and generate your audit packet." />

      {/* ── Single page-level tax year selector ── */}
      <div className="mb-5 flex items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="text-sm font-bold text-[#374151]">Tax year</span>
          <TaxYearInput value={pageTaxYear} onChange={(v) => { if (Number.isFinite(v)) setPageTaxYear(v); }} testid="page-tax-year" />
        </label>
      </div>

      <div className="space-y-5">
        {isOwner && activeStudents.length > 0 && (
          <YearAlignmentCard students={activeStudents} selectedStudentId={selectedStudentId} taxYear={pageTaxYear} onPickYear={setPageTaxYear} />
        )}
        {isOwner && activeStudents.length > 0 && (
          <AuditDefenseCard students={activeStudents} selectedStudentId={selectedStudentId} taxYear={pageTaxYear} />
        )}
        {/* Tax Year Data Entry — 1099-Q + tuition form */}
        {isOwner && activeStudents.length > 0 && (
          <TaxYearDataCard students={activeStudents} selectedStudentId={selectedStudentId} taxYear={pageTaxYear} onOpenDialog={(sid) => { setTyiDefaultStudent(sid || null); setTyiDlg(true); }} />
        )}
        {isOwner && activeStudents.length > 0 && (
          <div className="rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-3 py-2 text-[11px] text-[#034AA2]" data-testid="privacy-info-note">
            Enter your form values here &mdash; we don&rsquo;t ask you to upload your tax documents, so a Social Security number (yours or your beneficiary&rsquo;s) never has to leave your hands. We protect the information you do enter with security safeguards, though no system can be guaranteed against breaches.
          </div>
        )}
      </div>
      <TaxYearInputDialog open={tyiDlg} onClose={() => setTyiDlg(false)} students={activeStudents} defaultStudent={tyiDefaultStudent || selectedStudentId} taxYear={pageTaxYear} onSaved={tyiRefresh} />
    </div>
  );
}

// ─── Audit-Defense Packet ────────────────────────────────
const GAP_NAV = {
  missing_receipt: "/expenses",
  missing_coa_doc: "/students",
  missing_plan_statement: "/withdrawals",
  rb_over_cap: "/students",
  unacked_fx_correction: "/ledger",
  december_boundary: "/withdrawals",
  over_distribution: "/scholarships",
};


// ═══════════════════════════════════════════════════════════════════════════
//  YEAR ALIGNMENT  (ZEN-174)
// ═══════════════════════════════════════════════════════════════════════════
//
// WHAT THIS IS FOR. 529 money and the expense it paid for are meant to fall in the
// same calendar year. That is best practice, not law: two searches of IRS
// Publication 970 found no such rule, the tax code is silent on when a
// withdrawal must be taken, and the grace period proposed in Announcement
// 2008-17 was never finalised. So this panel STATES what it found and lets the
// account owner record why a pairing is deliberate. It never grades.
//
// NOT DRIVEN BY THE PAGE'S TAX-YEAR SELECTOR, deliberately. A mismatch is a
// disagreement BETWEEN two years, so filtering the panel to one of them would
// hide exactly the pairings it exists to show. One block per calendar year,
// all of them, every time.
function YearAlignmentCard({ students, selectedStudentId, taxYear, onPickYear }) {
  // THE PANEL ONLY EVER SHOWS A BENEFICIARY ITS OWN SELECTOR OFFERS.
  //
  // The global selection can still name an ARCHIVED beneficiary, and this
  // component is handed active ones only — so adopting it blindly let the panel
  // display and acknowledge findings for somebody its own dropdown could not
  // name, and whom the Overview's count deliberately excludes. Three surfaces,
  // one set. (Diff review, round 13.)
  const offered = (id) => students.some((st) => st.id === id);
  const first = (offered(selectedStudentId) && selectedStudentId)
    || (students[0] && students[0].id) || "";
  const [studentId, setStudentId] = useState(first);
  useEffect(() => {
    if (offered(selectedStudentId)) setStudentId(selectedStudentId);
    else if (!offered(studentId)) setStudentId(first);
  }, [selectedStudentId, students]); // eslint-disable-line
  const qc = useQueryClient();
  // THE YEAR THE PAGE IS SHOWING, ASKED FOR BY NAME. (ZEN-369)
  //
  // This panel used to ask for every year and render a block for each, on a
  // screen governed by a single tax-year picker every other panel obeys. The
  // picker said 2025 and the panel answered about 2026 — not wrong information,
  // information about a year nobody had asked about — and a beneficiary through
  // four to eight years of study produced four to eight blocks to scroll past.
  //
  // THE SERVER DOES THE FILTERING, which the owner settled rather than left to
  // the builder: it scales as the years accumulate, it matches the pattern the
  // rest of this screen already uses, and it keeps ONE definition of which year
  // a match belongs to. Re-deciding that here in JavaScript would be a second
  // decider beside the first, which is how this defect happened in the first
  // place.
  // THE SAME FLOOR THE PICKER ITSELF ALLOWS — 1996, the year section 529 was
  // enacted, which is the bound on every stored date in this app. A first draft
  // wrote 2000 here and made 1996-1999 unanswerable: a family with records in
  // those years would have had findings the panel simply refused to look at,
  // while the picker went on offering the year. A second bound invented beside
  // an existing one is the same defect this whole change is about.
  // (Adversarial diff review, round 1.)
  const validYear = Number.isFinite(taxYear) && taxYear >= 1996 && taxYear <= 2100;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["year-alignment", studentId, taxYear],
    queryFn: async () => (await api.get(
      `/students/${studentId}/year-alignment?tax_year=${taxYear}`)).data,
    enabled: !!studentId && validYear,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["year-alignment"] });
    qc.invalidateQueries({ queryKey: ["attention"] });
    qc.invalidateQueries({ queryKey: ["audit-readiness"] });
  };

  const block = (data?.years || [])[0] || null;
  const scope = data?.scope || null;
  // WHAT IS ACTUALLY WAITING FOR HER, which is not the same as what needs
  // looking at. A pairing that cannot be acknowledged is real work and counts on
  // the Overview, but telling her it is "waiting for your note" would be asking
  // for something the app will refuse. (Diff review, round 2.)
  const waiting = (block?.mismatches || []).filter(
    (c) => !c.acknowledged && c.acknowledgeable !== false).length;

  return (
    <Card data-testid="year-alignment-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#D97706] text-white"><Warning size={20} weight="bold" /></span>
          <div className="min-w-0">
            <div className="text-base font-extrabold">Year Alignment</div>
            <div className="text-xs text-[#6b7280]">
              {validYear
                ? `529 withdrawals in tax year ${taxYear}, and whether each is matched to an expense paid in the same calendar year.`
                : "529 withdrawals, and whether each is matched to an expense paid in the same calendar year."}
            </div>
          </div>
        </div>
        {students.length > 1 && (
          <select value={studentId} onChange={(e) => setStudentId(e.target.value)}
            className="rounded-md border border-[#C2C9D1] px-2 py-1 text-sm" data-testid="year-alignment-student">
            {students.map((st) => <option key={st.id} value={st.id}>{st.full_name}</option>)}
          </select>
        )}
      </div>

      <p className="mt-3 text-xs leading-snug text-[#6b7280]">
        Best practice is to take a withdrawal in the same calendar year the expense was paid. There is no deadline in the tax code, so where the expense was <b>already paid</b> the pairing is allowed &mdash; it just needs a note from you saying why, and recording the note also moves the expense to count toward the withdrawal&rsquo;s tax year on your totals and tax documents. The one exception is money taken out <i>before</i> the expense was paid, in an earlier year: that cannot be recorded as deliberate, and any such pairing below says so. Notes stay in this app and never appear in your tax packet.
      </p>
      {/* WHICH YEAR A MATCH ACROSS TWO YEARS COUNTS IN, said once, plainly.
          Without this the panel looks as though it has simply lost a pairing:
          she is looking at 2025, an expense she paid in 2025 was reimbursed in
          January 2026, and it is not here. It is under 2026, on purpose, and
          that is the same rule her totals already follow. */}
      <p className="mt-2 text-xs leading-snug text-[#6b7280]" data-testid="year-alignment-scope-note">
        A withdrawal that paid an expense from a different year is listed under the
        year the <b>withdrawal</b> was taken. Money drawn in 2026 against an expense paid
        in 2025 appears under 2026, because 2026 is the year you are filing it in.
      </p>

      <AlsoWaiting studentId={studentId} taxYear={taxYear} onPickYear={onPickYear} />

      {panelBody({ validYear, isLoading, isError, data }) === "no-year" ? (
        <div className="mt-4 text-sm text-[#6b7280]" data-testid="year-alignment-no-year">
          Choose a tax year above to check it.
        </div>
      ) : panelBody({ validYear, isLoading, isError, data }) === "checking" ? (
        <div className="mt-4 text-sm text-[#6b7280]">Checking&hellip;</div>
      ) : panelBody({ validYear, isLoading, isError, data }) === "unreachable" ? (
        <div className="mt-4 rounded-md border border-[#D97706]/30 bg-[#FCF5EE] px-3 py-2 text-sm text-[#B45309]"
          data-testid="year-alignment-unreachable">
          This check could not be run just now, so {taxYear} has not been checked
          at all. That is a problem on our side, not something in your records.
          Reload to try again.
        </div>
      ) : panelBody({ validYear, isLoading, isError, data }) === "server-behind" ? (
        // THE DEPLOY WINDOW, SAID OUT LOUD RATHER THAN RENDERED BLANK.
        //
        // The screens ship the moment a change is merged; the server waits for
        // an operator's click. So for as long as that gap lasts, THIS panel is
        // asking a server that does not yet know how to answer about one year —
        // it ignores the year and replies about the whole beneficiary, with no
        // `scope`. Rendering nothing in that case is the worst available
        // outcome: an empty panel on a tax screen reads as "nothing to see
        // here", which is the exact false reassurance this whole change exists
        // to prevent, and it would sit there for as long as the release is
        // held.
        //
        // It clears itself the moment the server catches up, and it asks for
        // nothing she cannot do.
        <div className="mt-4 rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-3 py-2 text-sm text-[#034AA2]"
          data-testid="year-alignment-server-behind">
          {/* IT MUST NOT SAY WHAT IT CANNOT KNOW. The first version added
              "nothing is wrong with your records", and this branch is taken for
              EVERY old-shape reply — including one where that server had already
              found things. A reassurance printed over unread findings is the
              exact failure this whole change exists to prevent, arriving through
              the sentence written to soften it. It now says only what is true,
              and passes on the old answer's own count when there is one. */}
          This check is being updated right now, so it cannot report on{" "}
          {taxYear} yet. Nothing has been lost &mdash; reload in a few minutes.
          {/* AND ONLY OVER A SCAN THAT FINISHED. The old server's count adds ONE
              when its own read fell short — that is OUR failure, and relaying it
              as "1 thing to look at" hands her a task she cannot do, which is
              the one line this whole change draws. (Follow-up review, round 2.) */}
          {data.complete === true && data.needs_attention > 0 && (
            <span>
              {" "}In the meantime, this beneficiary has{" "}
              <b>{data.needs_attention === 1 ? "1 thing" : `${data.needs_attention} things`}</b>{" "}
              to look at across all years.
            </span>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-4" data-testid="year-alignment-blocks">
          {isStaleAnswer({ isLoading, isError, data }) && (
            // THE ANSWER BELOW IS THE LAST GOOD ONE. Saying so is the difference
            // between a stale figure and a figure she thinks is current.
            <div className="rounded-md border border-[#D97706]/30 bg-[#FCF5EE] px-3 py-2 text-xs text-[#B45309]"
              data-testid="year-alignment-stale">
              This could not be refreshed just now, so what you see below is the
              last answer we had. Reload to try again.
            </div>
          )}
          <YearAlignmentAnswer scope={scope} taxYear={taxYear} />

          {(block?.mismatches || []).length > 0 && (
            <div className="space-y-2" data-testid={`year-alignment-block-${taxYear}`}>
              {block.mismatches.map((c) => (
                // KEYED ON THE FINGERPRINT, NOT ONLY THE PAIRING.
                //
                // A half-written note lives in this component's own state. Keyed on the
                // two record ids alone, the component SURVIVES a refresh that changed
                // the facts underneath it — so a note typed against one set of facts
                // stayed on screen while the fingerprint it would be submitted with
                // quietly became the new one, and the server accepted it as
                // confirmation of something she did not write it against. Including the
                // fingerprint makes React discard and rebuild the card when the facts
                // move, which clears the draft and mints a fresh key with it.
                // (Diff review, round 15.)
                <CrossYearCard key={`${c.expense_id}|${c.withdrawal_id}|${c.fingerprint || "none"}`}
                  card={c} studentId={studentId} onSaved={refresh} />
              ))}
            </div>
          )}
        </div>
      )}
      {waiting > 0 && (
        <div className="mt-3 text-[11px] font-semibold text-[#B45309]" data-testid="year-alignment-needs">
          {waiting === 1 ? "1 pairing is waiting for your note." : `${waiting} pairings are waiting for your note.`}
        </div>
      )}
    </Card>
  );
}

// WHICH OF THE PANEL'S FOUR BODIES TO SHOW. Exported and tested as a function,
// for the reason the sentences are: a check that reads this file's source for a
// string cannot tell a branch that RUNS from one that is merely still written
// down, and the sabotage run proved exactly that against the first attempt.
//
// "server-behind" IS THE DEPLOY WINDOW AND IT IS NOT THEORETICAL HERE. The
// screens ship the moment a change is merged; the server waits for an
// operator's click and a release can sit held for hours. Through that gap this
// panel asks a server that does not know how to answer about one year — it
// ignores the year and replies about the whole beneficiary, with no `scope`.
// Rendering nothing then is the worst available outcome: an empty panel on a tax
// screen reads as "nothing to see here", which is the exact false reassurance
// this change exists to prevent.
export function panelBody({ validYear, isLoading, isError, data }) {
  if (!validYear) return "no-year";
  if (isLoading) return "checking";
  // A CHECK THAT COULD NOT RUN IS NOT A CHECK THAT PASSED. React Query hands
  // back no data once its retries are exhausted, and rendering nothing then put
  // an empty panel on a tax screen for an ordinary network or server failure —
  // the swallowed-error-reads-as-clean shape, one floor down from where this
  // whole change fixed it. (Follow-up review.)
  //
  // ONLY WHEN THERE IS NOTHING TO SHOW, THOUGH. A FAILED REFRESH keeps the last
  // good answer AND sets the error flag, and treating that as unreachable threw
  // away a real result she was already looking at and told her the year had not
  // been checked at all — untrue, and a step backwards from what was on screen a
  // moment earlier. A stale answer is worth more than no answer, and the note
  // above it says it is stale. (Follow-up review, round 2.)
  if (!data) return "unreachable";
  return data.scope ? "answer" : "server-behind";
}


/** Is the answer on screen the last good one rather than a fresh one? */
export function isStaleAnswer({ isLoading, isError, data }) {
  return Boolean(isError && data && !isLoading);
}

// ── THE ONE ANSWER THIS TAX YEAR GETS ──────────────────────────────────────
//
// Five situations, five sentences, and they must not render identically.
//
// TWO OF THEM LOOK LIKE GOOD NEWS AND ONLY ONE IS EVIDENCE. "I checked and
// found nothing wrong" is a different claim from "there was nothing to check" —
// showing everything-lines-up over a year with no withdrawals implies an
// examination that never happened. That is the same standing rule that governs
// the tax packet: a claim about a year is one of clean, exceptions, or
// could-not-verify, and a swallowed error must never print as clean.
// (Owner rulings, ZEN-369.)
//
// THE WORDING AND THE COUNTS COME FROM THE SERVER'S DECISION, not from a second
// reading of the numbers here. This component chooses a sentence for a state it
// is handed; it never works out which state applies.
const ANSWER_LOOK = {
  no_withdrawals: { badge: "Nothing to check", cls: "text-[#374151] bg-[#F7F8F9] border-[#C2C9D1]" },
  none_matchable: { badge: "Nothing to check", cls: "text-[#374151] bg-[#F7F8F9] border-[#C2C9D1]" },
  all_same_year: { badge: "Everything lines up", cls: "text-[#077A39] bg-[#077A39]/10 border-[#077A39]/20" },
  cross_year: { badge: "Across years", cls: "text-[#B45309] bg-[#D97706]/10 border-[#D97706]/25" },
  needs_attention: { badge: "Needs attention", cls: "text-[#B45309] bg-[#D97706]/10 border-[#D97706]/25" },
};

// EXPORTED SO IT CAN BE TESTED AS THE FUNCTION IT IS. A substring check over
// this file's source is not a test of a sentence — the round-4 sabotage proved
// exactly that by disabling the guard while leaving the string in place, and the
// "test" stayed green. What matters is what a reader is shown, so the real
// inputs go in and the real sentence comes out.
export function answerSentence(scope) {
  const n = scope.withdrawals_in_year;
  const x = scope.cross_year_withdrawals;
  // HOW MANY RECORDS, NOT HOW MANY ROWS. One withdrawal can be both undated and
  // unmatched; "2 records need attention" over one record is a number she cannot
  // reconcile against the list underneath it. (Adversarial diff review, round 11.)
  const hers = Number.isFinite(scope.attention_records)
    ? scope.attention_records
    : (scope.attention || []).length;
  switch (scope.state) {
    case "no_withdrawals":
      return "No 529 withdrawals recorded in this tax year.";
    case "none_matchable":
      // THE SITUATION HER FOUR ANSWERS DO NOT COVER. A year holding only a
      // rollover, a student loan repayment or a withdrawal declared
      // non-qualified HAS withdrawals — so "none recorded" would be false — and
      // none of them is ever matched to an expense, so "all are matched" would
      // be false too. Given its own sentence rather than forced into one that
      // is not true.
      return `No 529 withdrawals in this tax year were taken to pay education expenses, so there is nothing to match.`;
    case "all_same_year": {
      // "PAID" IS A CLAIM ABOUT MONEY THAT HAS MOVED, and since ZEN-158 an expense
      // RECORDED BUT NOT YET PAID may legitimately be matched to a withdrawal —
      // the money has to leave the plan and reach a Canadian account before rent
      // is due. That is a supported flow, not a defect, so the badge stays
      // green and nothing lands on the to-do list. But saying "all 5 are
      // matched to expenses PAID in the same year" over one of them is simply
      // untrue, so the two kinds are counted and both are said. With none
      // waiting this collapses to the owner-approved sentence exactly.
      // (Adversarial diff review, round 1.)
      // TWO REASONS A MATCH CAN BE ALIGNED WITHOUT THE MONEY HAVING BEEN PAID
      // IN THIS YEAR, and neither is a defect:
      //   · an expense RECORDED BUT NOT YET PAID (ZEN-158) — the money has to leave
      //     the plan before rent is due;
      //   · an expense PAID IN AN EARLIER YEAR that she has confirmed belongs to
      //     this one (ZEN-331) — she paid the university in July and had the
      //     plan repay her in January, wrote her reason, and the cost now counts
      //     toward the withdrawal's year on every total.
      // The second is why the cross-year card correctly disappears once she
      // confirms it — and without naming it here the year fell straight through
      // to "matched to expenses PAID in the same year", claiming a payment year
      // that never happened. (Adversarial diff review, rounds 1 and 2.)
      const waitingOn = scope.awaiting_payment_withdrawals || 0;
      const confirmed = scope.label_aligned_withdrawals || 0;
      const paid = n - waitingOn - confirmed;
      if (!waitingOn && !confirmed) {
        return n === 1
          ? "The 1 withdrawal in this tax year is matched to an expense paid in the same year."
          : `All ${n} withdrawals in this tax year are matched to expenses paid in the same year.`;
      }
      const parts = [];
      if (paid > 0) {
        parts.push(`${paid} of ${n} withdrawal${n === 1 ? "" : "s"} in this tax year ${paid === 1 ? "is" : "are"} matched to an expense paid in the same year.`);
      }
      if (confirmed > 0) {
        parts.push(`${paid > 0 ? (confirmed === 1 ? "Another one is" : `Another ${confirmed} are`) : (confirmed === 1 ? "1 of " + n + " is" : `${confirmed} of ${n} are`)} matched to an expense paid in an earlier year that you have confirmed belongs to this one.`);
      }
      if (waitingOn > 0) {
        parts.push(`${(paid > 0 || confirmed > 0) ? (waitingOn === 1 ? "One is" : `${waitingOn} are`) : (waitingOn === 1 ? "1 of " + n + " is" : `${waitingOn} of ${n} are`)} matched to an expense you have recorded for this year but not yet paid.`);
      }
      return parts.join(" ");
    }
    case "cross_year":
      // THE PROPORTION ONLY WHEN THERE IS ONE. A withdrawal deleted while the
      // scan runs leaves its mismatch behind with nothing left to count it in,
      // and the numerator then exceeds the denominator — "1 of 0 withdrawals",
      // which is not a sentence about anything. The count in front is what tells
      // her instantly whether this is one oddity or a pattern, so it stays; the
      // false proportion goes. (Adversarial diff review, round 4.)
      return x > n
        ? `${x} withdrawal${x === 1 ? "" : "s"} ${x === 1 ? "is" : "are"} matched to an expense paid in a different year. Each one is listed below.`
        : `${x} of ${n} withdrawal${n === 1 ? "" : "s"} in this tax year ${x === 1 ? "is" : "are"} matched to an expense paid in a different year. Each one is listed below.`;
    case "needs_attention":
      if (hers === 0) {
        // OURS, NEVER HERS. A check that stopped early stopped because of
        // something inside this app. She cannot act on it, so it must not
        // appear as a task — that would put an item with no action behind it on
        // a tax to-do list. (Owner ruling.)
        return "This year could not be fully checked. This is a problem on our side, not something in your records — there is nothing here for you to do.";
      }
      return `This year could not be fully checked. ${hers} record${hers === 1 ? "" : "s"} need${hers === 1 ? "s" : ""} attention before it can be confirmed. Each one is listed below.`;
    default:
      return "This year could not be fully checked.";
  }
}

// SAID OUT LOUD SO THE NUMBER ABOVE IS ONE SHE CAN RECONCILE. If the panel
// counts five and the 529 Distributions page shows six, the difference has to be
// on the screen rather than left as a puzzle.
//
// EXPORTED AND TESTED AS A SENTENCE, like answerSentence and for the same
// reason: this one read "2 other withdrawals in this year WAS recorded" until a
// review round caught it, and a substring check over the source would not have.
export function otherWithdrawalsNote(n) {
  const subject = n === 1 ? "1 other withdrawal in this year was"
    : `${n} other withdrawals in this year were`;
  const object = n === 1 ? "it is" : "they are";
  return `${subject} recorded for another purpose — a rollover, a student loan `
    + `repayment, or one you declared non-qualified. Expenses are never matched `
    + `to those, so ${object} not counted above.`;
}

/** The other years where the Overview's Tax Forms warnings have items for
 *  this beneficiary, each one a click away. (ZEN-768, ZEN-773) */
function AlsoWaiting({ studentId, taxYear, onPickYear }) {
  const { data: attention } = useAttention(studentId, { enabled: !!studentId });
  const others = otherYearsWaiting(attention, studentId, taxYear);
  if (!others.length || !onPickYear) return null;
  return (
    <div className="mt-3 space-y-1" data-testid="year-alignment-also-waiting">
      {others.map((w) => (
        <p key={w.year} className="rounded-md border border-[#D97706]/30 bg-[#FCF5EE] px-3 py-2 text-xs text-[#92600A]">
          {ALSO_WAITING_IN}{" "}
          <button type="button" onClick={() => onPickYear(Number(w.year))}
            className="font-semibold text-[#034AA2] underline" data-testid={`year-alignment-also-waiting-${w.year}`}>
            {w.year}
          </button>{" "}
          {alsoWaitingIn(w.count)}
        </p>
      ))}
    </div>
  );
}

function YearAlignmentAnswer({ scope, taxYear }) {
  const look = ANSWER_LOOK[scope.state] || ANSWER_LOOK.needs_attention;
  const rows = scope.attention || [];
  const ours = scope.our_defect;
  return (
    <div className="rounded-lg border border-[#C2C9D1]" data-testid={`year-alignment-answer-${taxYear}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#C2C9D1] px-3 py-2">
        <span className="text-sm font-extrabold">Tax year {taxYear}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${look.cls}`}
          data-testid={`year-alignment-state-${taxYear}`}>{look.badge}</span>
      </div>
      <div className="space-y-2 p-3">
        <p className="text-xs font-semibold text-[#374151]" data-testid="year-alignment-answer-text">
          {answerSentence(scope)}
        </p>
        {scope.other_withdrawals_in_year > 0 && (
          <p className="text-[11px] text-[#6b7280]" data-testid="year-alignment-other-withdrawals">
            {otherWithdrawalsNote(scope.other_withdrawals_in_year)}
          </p>
        )}
        {ours && (
          <div className="rounded-md border border-[#D97706]/30 bg-[#FCF5EE] px-3 py-2 text-[11px] text-[#B45309]"
            data-testid="year-alignment-incomplete">
            {/* THE COUNT ONLY APPEARS WHEN THERE IS ONE. Three separate reads
                can fall short and only one of them carries a scanned/expected
                pair, so printing it unconditionally rendered the literal
                "undefined of undefined" at the account owner — a defect
                introduced by the previous round's fix to the same sentence.
                (Adversarial diff review, round 3.) */}
            {ours.kind === "repair_in_progress"
              ? "Your records are being re-filed into their tax years right now, so this year cannot be confirmed until that finishes. Nothing is wrong — check again in a few minutes."
              : Number.isFinite(ours.scanned) && Number.isFinite(ours.expected)
                ? `We could not read all of your records just now (${ours.scanned} of ${ours.expected}), so this year cannot be reported as fully checked. This is ours to fix, not yours. Reload to try again.`
                : "We could not read all of your records just now, so this year cannot be reported as fully checked. This is ours to fix, not yours. Reload to try again."}
          </div>
        )}
        {rows.length > 0 && (
          <div className="space-y-1.5" data-testid="year-alignment-attention">
            {rows.map((r, i) => (
              <AttentionRow key={`${r.kind}|${r.record_id || "none"}|${i}`} row={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ONE ROW OF THE TO-DO LIST ───────────────────────────────────────────────
//
// A COULD-NOT-BE-CHECKED YEAR IS A TO-DO LIST, NOT A STATUS. (Owner ruling.)
// At tax time an unexplained cannot-confirm is worse than useless — it is an
// alarm with no off switch. So every row names the record the way the account
// holder recognises it (payee, amount, date — never an internal identifier she
// cannot search for), says what is wrong with it, and links to the screen where
// she can fix it.
//
// NOTHING OURS EVER REACHES THIS COMPONENT. A scan that stopped early because
// of something inside this app is reported separately and filed as our defect;
// presenting it here would be passing the buck.
const payeeName = (p) => (String(p ?? "").trim() || "an expense with no payee recorded");

export function AttentionRow({ row }) {
  const isWithdrawal = row.record_type === "withdrawal";
  const amountText = row.amount_known === false ? null : money(row.amount || 0, "USD");
  const when = row.date ? fmtDate(row.date) : null;

  const name = isWithdrawal
    ? `Withdrawal${amountText ? ` of ${amountText}` : ""}${when ? ` on ${when}` : ""}`
    : `${payeeName(row.payee)}${when ? ` (${when})` : ""}`;

  const problem = {
    withdrawal_unmatched: "Not matched to any expense — this may be a taxable event.",
    expense_date_unreadable: row.raw_date
      ? `The payment date reads “${row.raw_date}”, which the app cannot read as a full date. Open it and set a year, month and day.`
      : "The payment date cannot be read, so the two years cannot be compared. Open it and set a full date.",
    distribution_date_unreadable: "The withdrawal this is matched to has no readable date, so the two years cannot be compared. Open the withdrawal and set the date the money left the plan.",
    // THE ROW IS THE WITHDRAWAL ITSELF, and it may have no match at all — so it
    // cannot borrow the sentence above, which is written for an expense row and
    // says "the withdrawal this is matched to". A row's words follow what the
    // row IS. (Adversarial diff review, round 4.)
    withdrawal_own_date_unreadable: row.has_year
      ? "This withdrawal has no date the app can read. The tax year it is filed under has nothing behind it until there is one, so this year cannot be confirmed. Open it and set the date the money left the plan."
      : "This withdrawal has no date the app can read and no tax year recorded, so it cannot be placed in a tax year at all — it is shown under every year until it is fixed. Open it and set the date the money left the plan.",
    // ITS DATE IS FINE AND ITS FILING IS NOT. Nothing is wrong with the date, so
    // this row must not tell her to go and fix one. (Round 16.)
    withdrawal_year_unusable: "This withdrawal is filed under a tax year outside the range this app can report on, so it is counted in no year at all. Open it and correct the year it is filed under.",
    // ITS YEAR WAS WORKED OUT, NOT READ. Every total in the app and the tax
    // packet's own list group by the year stored on the record; this one has
    // none, or has it in a shape those cannot match, so the document leaves it
    // out while this panel can still see it. (Round 17.)
    withdrawal_year_not_stored: `This withdrawal has no tax year stored on it that your totals and your tax packet can use${row.filed_year ? ` — this check reads it as ${String(row.filed_year)} from its dates` : ""}, so it is left out of the packet for that year. Open it and re-save it with the date the money left the plan.`,
    // ITS TWO ANSWERS ABOUT ITS OWN YEAR DISAGREE. Every total groups by the
    // year it is filed under while the same-year check reads its dates, so
    // neither year over it can be called clean until one of them is corrected.
    withdrawal_year_disputed: `This withdrawal is filed under ${String(row.stored_tax_year)} but its own dates say ${String(row.date_year)}. Your year totals follow the first and this check follows the second, so neither year can be confirmed until they agree. Open it and correct the date.`,
    withdrawal_missing: "The withdrawal this expense points at is no longer there. Open the expense and match it to a withdrawal that exists, or remove the match.",
    withdrawal_other_beneficiary: "This expense is matched to a withdrawal recorded for a different beneficiary. Open it and match it to one of this beneficiary’s withdrawals.",
    amount_unreadable: "The amount stored on this expense is not a number the app can read. Open it and enter the amount again.",
    match_marker_unreadable: "One of this expense’s matches is stored in a shape the app cannot read. Open it and record the match again.",
    label_matches_neither: `This expense is filed under ${String(row.stored_tax_year)}, which matches neither the year it was paid nor any withdrawal it is tied to, so the year totals it sits in are wrong until it is fixed. Open it and correct the year.`,
    witness_invalid: "This expense says its year follows a withdrawal that no longer backs it, so the year totals it sits in are wrong until it is fixed. Open it and correct the year.",
  }[row.kind] || "This record could not be checked.";

  const where = row.fix_page === "/withdrawals" ? "529 Distributions" : "Expenses";

  return (
    <div className="rounded border border-[#D97706]/30 bg-[#FCF5EE] px-3 py-2 text-xs"
      data-testid={`year-alignment-attention-${row.kind}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-bold text-[#111827]">{name}</span>
        <Link to={row.fix_page} className="shrink-0 text-[11px] font-semibold text-[#034AA2] hover:underline">
          Open the {where} page
        </Link>
      </div>
      <div className="mt-0.5 text-[#B45309]">{problem}</div>
      {row.kind === "withdrawal_unmatched" && <UnmatchedWithdrawalPaths />}
    </div>
  );
}

// ── THE THREE THINGS THAT COULD HAVE HAPPENED, AND WHAT TO DO ABOUT EACH ────
//
// WHY THREE AND NOT TWO. An earlier draft offered "re-match it or remove the
// match", and the owner corrected it: a 529 withdrawal that ACTUALLY HAPPENED
// cannot simply be removed — the money left the account and the record has to
// reflect that. Only a withdrawal entered in error may be deleted. If it
// happened and there is nothing eligible to tie it to, that is a tax event and
// the app must say so carefully without giving advice.
//
// WHAT THE RECORDS CAN AND CANNOT PROVE, said honestly. A match is stored on
// the EXPENSE and deleting an expense removes it outright, leaving nothing
// behind on the withdrawal. So "this was matched and the expense was deleted"
// and "this was never matched" are indistinguishable afterwards — and the
// wording below says exactly that rather than asserting a deletion nobody can
// evidence. All three paths apply either way.
//
// THE TAX SENTENCE IS VERIFIED, NOT REMEMBERED. IRS Tax Topic 313 and
// Publication 970's 529 chapter: where a distribution exceeds qualified
// expenses, "a portion of the EARNINGS is taxable" — the contributions coming
// back are not. IRS Form 5329 Part II is where the 10% is actually paid: line 5
// is "Distributions included in income" and line 8 is "Enter 10% (0.10) of line
// 7", line 7 being line 5 less the exceptions. So the 10% is charged on the
// earnings that were taxed and NEVER on the whole withdrawal. Saying only
// "part of it taxable and a 10% penalty" reads as 10% of the withdrawal, which
// on a $7,553.94 record invents about $755 of fear over a real figure that is
// usually a small fraction of it. (ZEN-369; the owner asked for the check and
// for the source to win.)
function UnmatchedWithdrawalPaths() {
  return (
    <details className="mt-1.5" data-testid="year-alignment-unmatched-paths">
      <summary className="cursor-pointer text-[11px] font-semibold text-[#034AA2]">
        What to do about this
      </summary>
      <div className="mt-1 space-y-1.5 text-[11px] leading-snug text-[#374151]">
        <p>
          This app has no record of an expense matched to this withdrawal. That is
          either because it was never matched, or because the expense it was matched
          to has since been deleted &mdash; your records cannot tell those two apart.
          What to do depends on what actually happened:
        </p>
        <p>
          <b>If the expense is still in your records</b> &mdash; open this withdrawal
          on the 529 Distributions page and match it again.
        </p>
        <p>
          <b>If this withdrawal was entered by mistake and never happened</b> &mdash;
          delete it.
        </p>
        <p>
          <b>If the money really was withdrawn and there is no qualifying expense to
          match it to</b> &mdash; it may count as a non-qualified distribution. Only
          the <b>earnings</b> part of the withdrawal is taxable; the money you
          originally put in comes back to you untaxed. The extra 10% is charged on
          that same earnings part too, not on the whole withdrawal. Your plan reports
          the earnings figure in box 2 of the 1099-Q it sends you. Zen 529 keeps your
          records; it is not a tax adviser. Ask yours how this applies to you before
          you file.
        </p>
      </div>
    </details>
  );
}

// WHAT AN EXPENSE WITH NO PAYEE LOOKS LIKE — decided here, in the browser, and
// nowhere else. The server used to substitute a placeholder before sending the
// card, which read fine on screen and quietly broke the save: the route that
// records a note rebuilds its facts from the card, so it hashed the placeholder
// while the detector hashed the real (empty) value, and a note about such an
// expense could never count. The server now sends what is stored. The screen is
// the only place a stand-in is safe. (Diff review, round 24.)
const payeeLabel = (p) => (String(p ?? "").trim() || "No payee recorded");

// The verdict badge, the one-block-per-year frame and the table of
// unverifiable sentences that used to live here are gone: this panel now shows
// exactly one year and gives it exactly one answer, and every finding inside
// that year is a row on the to-do list above with what to do about it. A
// sentence that only said what was wrong, with no next step, is what the owner
// ruled out. (ZEN-369)

function CrossYearCard({ card, studentId, onSaved }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // ── Correcting a note ─────────────────────────────────────────────────────
  //
  // "Corrections APPEND a new entry" is the store's promise, and for one round
  // this screen made it impossible to keep: once a pairing was acknowledged the
  // input disappeared, and the retry key was pinned to the fingerprint, so even
  // reposting returned the original row. A trail nobody can correct is not an
  // append-only trail, it is a locked one. (Diff review, round 7.)
  const [correcting, setCorrecting] = useState(false);
  // PER CORRECTION, NOT PER COUNT. Pinned to the fingerprint alone, a second,
  // deliberate note about the same facts was silently answered with the first.
  // A counter fixed that and introduced a quieter version of it: the counter
  // starts at zero again on every remount, so a correction written after a
  // reload reused the very first key and the server — comparing fingerprints,
  // which still matched — handed back the original note and reported success.
  // The correction vanished and the account owner was told it had saved.
  //
  // The key is minted when she OPENS a correction, so each one is its own
  // entry however many times the page has been reloaded, while a double-click
  // inside one open form still collapses to a single row, which is the whole
  // point of having a key. (Diff review, round 8.)
  const newKey = () => (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  // MINTED ON MOUNT, NOT A FIXED WORD. The first note used the literal "first",
  // which is the same string in every tab — so two tabs left open on the same
  // unchanged pairing, each with a different note, collapsed to one row and the
  // second was told it had saved. One key per open card: two tabs are two
  // decisions and get two entries, while a double-click in one tab is one
  // decision and still gets one. (Diff review, round 10.)
  const [correctionKey, setCorrectionKey] = useState(() => newKey());
  const retryKey = `card:${card.fingerprint || "none"}:${correctionKey}`;

  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data: ackRes } = await api.post(`/students/${studentId}/year-alignment/acknowledgements`, {
        expense_id: card.expense_id,
        withdrawal_id: card.withdrawal_id,
        retry_key: retryKey,
        note: note.trim(),
        // COMPARE-ONLY. The server computes and stores its own; this one only
        // says "these are the facts I was shown", so a save made after the
        // records moved under this page is refused instead of recording a
        // decision about something the account owner never saw.
        observed_fingerprint: card.fingerprint,
      });
      // The server answers whether the accounting effect actually happened.
      // "Note saved" over a label that could not move would present the
      // decision as complete while the totals still disagree. (ZEN-331)
      if (ackRes?.label_moved) {
        toast.success("Note saved — this expense now counts toward the withdrawal's tax year.");
      } else {
        toast.warning(ackRes?.label_note ||
          "Your note is recorded, but the expense still counts toward the year it was paid. Open the expense to move its year.");
      }
      setNote("");
      setCorrecting(false);
      setCorrectionKey(newKey());
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const ack = card.acknowledgement;
  return (
    <div className={`rounded border p-3 ${card.acknowledged ? "border-[#C2C9D1] bg-white" : "border-[#D97706]/30 bg-[#FCF5EE]"}`}
      data-testid={`year-alignment-card-${card.expense_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 text-xs">
          <div className="break-words font-bold text-[#111827]">{payeeLabel(card.payee)}</div>
          <div className="mt-0.5 text-[#6b7280]">
            Paid {card.expense_date || "date not recorded"} &middot; withdrawal {card.distribution_date || "date not recorded"}
          </div>
        </div>
        <div className="text-right text-xs">
          <div className="tnum font-bold">{card.amount_known ? money(card.amount_usd, "USD") : "amount not recorded"}</div>
          <div className="mt-0.5 whitespace-nowrap text-[10px] font-semibold text-[#B45309]">
            expense {card.expense_year} &rarr; withdrawal {card.distribution_year}
          </div>
        </div>
      </div>

      {card.acknowledgeable === false ? (
        // NOT EVERY MISMATCH MAY BE ACKNOWLEDGED, and offering a note box for one
        // that may not is worse than offering nothing: the account owner writes
        // an explanation, presses save, and is refused by a rule the screen never
        // mentioned. Money out in December against an expense due in January is the
        // one pairing the app refuses outright — there is no note that makes it
        // allowed, and saying so plainly is the only honest thing here.
        // (Diff review, round 2.)
        <div className="mt-2 rounded bg-[#FCF5EE] px-2 py-1.5 text-[11px] text-[#B45309]"
          data-testid={`year-alignment-not-acknowledgeable-${card.expense_id}`}>
          This withdrawal was taken before the expense was paid, in an earlier calendar
          year. That pairing cannot be recorded as deliberate. Move the withdrawal
          into the year the expense was paid, or match it to an expense paid in its own year.
        </div>
      ) : card.acknowledged && ack && !correcting ? (
        <div className="mt-2 rounded bg-[#F7F8F9] px-2 py-1.5 text-[11px]" data-testid={`year-alignment-note-${card.expense_id}`}>
          <div className="font-semibold text-[#077A39]">Recorded</div>
          <div className="mt-0.5 break-words text-[#374151]">&ldquo;{ack.note}&rdquo;</div>
          <div className="mt-0.5 text-[10px] text-[#6b7280]">{ack.created_by_email} &middot; {(ack.created_at || "").slice(0, 10)}</div>
          {/* A correction ADDS an entry; it never rewrites this one. Both stay
              visible, which is the only reason the trail is worth keeping. */}
          <button onClick={() => { setCorrectionKey(newKey()); setCorrecting(true); }}
            className="mt-1 text-[10px] font-semibold text-[#034AA2] hover:underline"
            data-testid={`year-alignment-correct-${card.expense_id}`}>
            Add a correction
          </button>
        </div>
      ) : (
        <div className="mt-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Why do these belong together?</span>
            <textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="For example: paid the university early for next year's study."
              className="w-full rounded border border-[#C2C9D1] px-2 py-1 text-xs"
              data-testid={`year-alignment-input-${card.expense_id}`} />
          </label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Btn variant="green" className="h-8" disabled={!note.trim() || saving} onClick={save}
              data-testid={`year-alignment-save-${card.expense_id}`}>{saving ? "Saving\u2026" : (correcting ? "Add this correction" : "Record this note")}</Btn>
            {correcting && (
              <button onClick={() => { setCorrecting(false); setNote(""); }}
                className="text-[11px] font-semibold text-[#6b7280] hover:underline"
                data-testid={`year-alignment-cancel-${card.expense_id}`}>Cancel</button>
            )}
          </div>
          {correcting && (
            <p className="mt-1 text-[10px] text-[#6b7280]">
              Your earlier note stays on the record; this is added beside it.
            </p>
          )}
        </div>
      )}

      {(card.superseded || []).length > 0 && (
        <details className="mt-2 text-[11px]" data-testid={`year-alignment-history-${card.expense_id}`}>
          <summary className="cursor-pointer font-semibold text-[#6b7280]">
            Earlier notes ({card.superseded.length})
          </summary>
          <div className="mt-1 space-y-1">
            {card.superseded.map((h) => (
              // WHY EACH ONE IS HERE, said per entry rather than once at the top.
              // The heading used to claim they were all kept "because the records
              // have changed since", which is untrue of a correction the account
              // holder wrote herself about unchanged records — and this list holds
              // both kinds. Each row now says which it is, from its own facts.
              <div key={h.id} className="rounded bg-[#F7F8F9] px-2 py-1">
                <div className="break-words text-[#374151]">&ldquo;{h.note}&rdquo;</div>
                <div className="text-[10px] text-[#6b7280]">
                  {h.created_by_email} &middot; {(h.created_at || "").slice(0, 10)}
                  {h.fingerprint && card.fingerprint && h.fingerprint === card.fingerprint
                    ? " · replaced by a later note about the same records"
                    : ` · the records then read: paid ${h.expense_date}, withdrawal ${h.distribution_date}`}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// WHICH OF THE READINESS CARD'S BODIES TO SHOW. (ZEN-327) The same rule as
// panelBody above: a checklist that could not be run is not a checklist that
// came back clean. That holds for a re-check too: once the latest check has
// been refused, an earlier "all checks passed" is no longer what is known.
export function readinessBody({ enabled, isLoading, isError, data }) {
  if (!enabled) return "none";
  if (isLoading) return "checking";
  if (isError || !Array.isArray(data?.gaps)) return "unchecked";
  return data.gaps.length > 0 ? "gaps" : "clear";
}

export function readinessNotChecked(taxYear) {
  return `The readiness checklist could not be run just now, so this page cannot say whether anything is missing for ${taxYear}.`;
}
export const READINESS_TRY_AGAIN = "Reload to try again.";

export function AuditDefenseCard({ students, selectedStudentId, taxYear }) {
  const [selectedStudent, setSelectedStudent] = useState(selectedStudentId || "");
  React.useEffect(() => { setSelectedStudent(selectedStudentId || ""); }, [selectedStudentId]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  // THE SAME FLOOR THE PICKER OFFERS, 1996 — section 529's enactment and the
  // bound on every stored date in this app. This card required 2000 while the
  // picker offered 1996, so a family with records in those four years was shown
  // a year whose readiness was never fetched and whose packet could not be
  // generated. Two bounds beside one picker is the defect this ticket is about.
  // (Adversarial diff review, round 17.)
  const validYear = Number.isFinite(taxYear) && taxYear >= 1996 && taxYear <= 2100;

  const readinessEnabled = !!selectedStudent && validYear;
  const { data: readiness, isLoading, isError, error: readinessError } = useQuery({
    queryKey: ["audit-readiness", selectedStudent, taxYear],
    queryFn: async () => (await api.get(`/students/${selectedStudent}/audit-packet/readiness?tax_year=${taxYear}`)).data,
    enabled: readinessEnabled,
    staleTime: 30_000,
  });
  const body = readinessBody({ enabled: readinessEnabled, isLoading, isError, data: readiness });

  const gaps = body === "gaps" ? readiness.gaps : [];
  const hasGaps = gaps.length > 0;
  const hasError = gaps.some((g) => g.severity === "error");

  const doGenerate = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    try {
      await downloadFile(`/students/${selectedStudent}/audit-packet`,
        (resp) => resp.headers?.["content-disposition"]?.match(/filename="(.+)"/)?.[1] || `529-packet-TY${taxYear}.zip`,
        { tax_year: taxYear });
      toast.success("Audit-Defense Packet downloaded");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setGenerating(false);
    }
  };

  const onGenerateClick = () => {
    if (hasGaps) {
      setConfirmOpen(true);
    } else {
      doGenerate();
    }
  };

  return (
    <Card data-testid="audit-defense-card">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#034AA2] text-white">
          <ShieldCheck size={20} weight="bold" />
        </span>
        <div className="flex-1">
          <div className="text-base font-extrabold" data-testid="audit-defense-title">Audit-Defense Packet</div>
          <div className="text-xs text-[#6b7280]">Generate a comprehensive ZIP with a master PDF and all supporting exhibits for IRS documentation.</div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-0.5 block text-[10px] font-bold uppercase text-[#6b7280]">Beneficiary</span>
          <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)}
            data-testid="audit-student-select"
            className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm">
            <option value="">Choose a beneficiary…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        </label>
        <Btn onClick={onGenerateClick} disabled={generating || isLoading || !validYear || !selectedStudent}
          data-testid="audit-generate-btn"
          className="h-9">
          {generating ? "Generating…" : "Generate packet"}
        </Btn>
      </div>

      {/* Readiness checklist */}
      {body === "checking" ? (
        <div className="mt-4 text-sm text-[#6b7280]">Checking readiness…</div>
      ) : body === "unchecked" ? (
        <div className="mt-4 rounded-md border border-[#D97706]/30 bg-[#FCF5EE] px-3 py-2 text-sm text-[#B45309]"
          data-testid="audit-readiness-unchecked">
          {readinessNotChecked(taxYear)}{" "}
          {refusalSentence(readinessError) || READINESS_TRY_AGAIN}
        </div>
      ) : body !== "none" && (
        <div className="mt-4" data-testid="audit-readiness-list">
          {body === "clear" ? (
            <div className="flex items-center gap-2 rounded-lg border border-[#077A39]/20 bg-[#077A39]/5 px-3 py-2.5 text-sm font-semibold text-[#077A39]" data-testid="audit-all-clear">
              <CheckCircle size={18} weight="fill" />
              All checks passed — ready to generate
            </div>
          ) : (
            <div className="space-y-1.5">
              {gaps.map((g, i) => (
                <div key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                  g.severity === "error"
                    ? "border-[#F01919]/20 bg-[#F01919]/5 text-[#6F0A11]"
                    : "border-[#D97706]/20 bg-[#FCF5EE] text-[#92600A]"
                }`} data-testid={`audit-gap-${g.type}`}>
                  {g.severity === "error"
                    ? <XCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
                    : <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />}
                  <span className="flex-1">{g.description}</span>
                  {g.fix_page && (
                    <a href={g.fix_page} className="ml-2 shrink-0 text-[#034AA2] hover:underline" data-testid={`audit-gap-fix-${g.type}`}>
                      <ArrowSquareOut size={14} weight="bold" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirm dialog for generating with gaps */}
      <DecisionDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doGenerate}
        title={hasError ? "Generate with over-distribution?" : "Generate with known gaps?"}
        actionLabel="Generate anyway"
        testid="audit-confirm-gaps"
      >
        <p className="text-sm text-[#374151]">{hasError
          ? "The packet will include an over-distribution notice and a taxable-earnings estimate. You can fix the gaps and regenerate at any time."
          : "The packet will include a Known Gaps page listing what's missing. You can fix the gaps and regenerate at any time."}</p>
      </DecisionDialog>
    </Card>
  );
}


// ─── Tax Year Data Entry Card ────────────────────────────
function TaxYearDataCard({ students, selectedStudentId, taxYear, onOpenDialog }) {
  const [studentId, setStudentId] = useState(selectedStudentId || "");
  React.useEffect(() => { setStudentId(selectedStudentId || ""); }, [selectedStudentId]);

  const selectedStudent = students.find(s => s.id === studentId);
  const formLabel = tuitionFormLabel(selectedStudent);

  const { data: dashboard } = useQuery({
    queryKey: ["dashboard-tax-year", taxYear, studentId],
    queryFn: async () => (await api.get(`/dashboard/tax-year/${taxYear}${studentId ? `?student_id=${studentId}` : ""}`)).data,
    enabled: !!taxYear && !!studentId,
  });
  const stuData = dashboard?.students?.find(s => s.student.id === studentId);
  const aqee = stuData?.aqee;
  const r = aqee?.recon_1098t;

  return (
    <Card data-testid="tax-year-data-card">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#034AA2] text-white"><PencilSimple size={20} weight="bold" /></span>
        <div className="flex-1">
          <div className="text-base font-extrabold">Tax Year Data Entry</div>
          <div className="text-xs text-[#6b7280]">Enter your 1099-Q and {formLabel} box values for Adjusted Qualified Education Expenses (AQEE) calculations.</div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="block"><span className="mb-0.5 block text-[10px] font-bold uppercase text-[#6b7280]">Beneficiary</span>
          <select value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="tyd-student" className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm"><option value="">Choose…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></label>
        <Btn onClick={() => onOpenDialog(studentId)} data-testid="tax-year-inputs-btn" className="h-9">1099-Q / {formLabel}</Btn>
      </div>

      {/* AQEE summary line */}
      {aqee && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="text-[#6b7280]">Qualified: <b className="text-[#0A0A0A]">{money(aqee.total_qualified, "USD")}</b></span>
          {aqee.tax_free_assistance > 0 && <span className="text-[#D97706]">Less aid: <b>{money(aqee.tax_free_assistance, "USD")}</b></span>}
          {aqee.aotc_llc_reserved > 0 && <span className="text-[#D97706]">Less AOTC: <b>{money(aqee.aotc_llc_reserved, "USD")}</b></span>}
          <span className="font-extrabold text-[#077A39]">AQEE: {money(aqee.aqee, "USD")}</span>
          {aqee.has_1099q && <>
            <span className="text-[#6b7280]">1099-Q: dist {money(aqee.box1_gross_distribution, "USD")}, earnings {money(aqee.box2_earnings, "USD")}</span>
            {aqee.taxable_earnings > 0 && <span className="font-bold text-[#F01919]">Taxable: {money(aqee.taxable_earnings, "USD")}</span>}
          </>}
        </div>
      )}

      {/* 1098-T reconciliation */}
      {r && (
        <div className="mt-3 rounded-md border border-[#C2C9D1] bg-[#F4F7FB] px-3 py-2" data-testid={`recon-1098t-${studentId}`}>
          <div className="text-[11px] font-bold uppercase text-[#6b7280]">1098-T Reconciliation</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-1">Box 1 (tuition): {money(r.box1_tuition, curCode(r.tuition_currency, "USD"))} vs recorded {money(r.recorded_tuition, curCode(r.tuition_currency, "USD"))} {r.tuition_match ? <CheckCircle size={14} className="text-[#077A39]" weight="bold" /> : <span className="font-bold text-[#D97706]">diff {money(r.tuition_difference, curCode(r.tuition_currency, "USD"))}</span>}</span>
            <span className="flex items-center gap-1">Box 5 (aid): {money(r.box5_scholarships, "USD")} vs recorded {money(r.recorded_scholarships, "USD")} {r.scholarship_match ? <CheckCircle size={14} className="text-[#077A39]" weight="bold" /> : <span className="font-bold text-[#D97706]">diff {money(r.scholarship_difference, "USD")}</span>}</span>
          </div>
          {(!r.tuition_match || !r.scholarship_match) && (
            <p className="mt-1 text-[10px] text-[#92400E]">Discrepancies may indicate missing expenses or scholarships. Review and reconcile before filing.</p>
          )}
        </div>
      )}
    </Card>
  );
}


/** The currency the tuition document box shows and saves (ZEN-820): the one
 *  picked or stored; a stored figure with none is USD; a new figure starts in
 *  CAD for a Canadian school, the school's currency elsewhere. A US school's
 *  1098-T is always USD. */
export function tuitionDocCurrency({ supported, isCanada, isForeign, schoolCurrency, hasStoredFigure, picked }) {
  if (!supported || !isForeign) return "USD";
  if (curCode(picked)) return curCode(picked);
  if (hasStoredFigure) return "USD";
  if (isCanada) return "CAD";
  return (isForeign && curCode(schoolCurrency)) || "USD";
}

// ─── Tuition reconciliation row (shown beneath each tuition input) ─────
export function TuitionReconRow({ recon, taxYear }) {
  if (!recon) return null;
  const recorded = recon.recorded_tuition ?? 0;
  const match = recon.tuition_match;
  const diff = recon.tuition_difference;
  const hasDiff = diff != null && Math.abs(diff) >= 1.0;
  const cur = curCode(recon.tuition_currency, "USD");
  const others = recon.other_currency_tuition_count || 0;
  return (
    <>
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]" data-testid="tuition-recon-row">
      <span className="text-[#6b7280]">Tuition and fees recorded in Expenses for {taxYear}:</span>
      <span className="font-bold text-[#0A0A0A]">{money(recorded, cur)}</span>
      {match && <CheckCircle size={14} className="text-[#077A39]" weight="bold" data-testid="tuition-recon-match" />}
      {match && <span className="font-semibold text-[#077A39]">Matches</span>}
      {hasDiff && <span className="font-bold text-[#D97706]" data-testid="tuition-recon-diff">diff {money(diff, cur)}</span>}
    </div>
    {others > 0 && (
      <p className="mt-0.5 text-[11px] text-[#92400E]" data-testid="tuition-recon-other-currency">
        {others === 1
          ? `1 tuition expense for ${taxYear} is recorded in a currency other than ${cur}, so it is not part of this comparison. Record it in the currency shown on its receipt.`
          : `${others} tuition expenses for ${taxYear} are recorded in a currency other than ${cur}, so they are not part of this comparison. Record each one in the currency shown on its receipt.`}
      </p>
    )}
    </>
  );
}

// ─── Tuition document amount: the figure as printed, in the document's currency (ZEN-820) ─────
function TuitionDocAmount({ label, currency, onCurrency, value, onChange, placeholder, testid }) {
  return (
    <div className="block">
      <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>
      <div className="flex gap-2">
        {onCurrency && <div className="w-28 shrink-0"><CurrencySelect value={currency} onChange={(c) => onCurrency(curCode(c))} testid={`${testid}-currency-select`} /></div>}
        <MoneyInput currency={currency} value={value} onChange={onChange} placeholder={placeholder} testid={testid} />
      </div>
    </div>
  );
}

// ─── Tax Year Input Dialog (moved from Scholarships) ─────
function TaxYearInputDialog({ open, onClose, students, defaultStudent, taxYear, onSaved }) {
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState("");

  useFillOnOpen(open, "tax-year-figures", () => setStudentId(defaultStudent || ""));

  const { data: inputs = [] } = useQuery({
    queryKey: ["tax-year-inputs", studentId, taxYear],
    queryFn: async () => (await api.get(`/tax-year-inputs?student_id=${studentId}&tax_year=${taxYear}`)).data,
    enabled: open && !!studentId && !!taxYear,
  });
  const existing = inputs[0] || {};

  // Fetch AQEE/recon data (includes recorded_tuition from expenses)
  const { data: reconDash } = useQuery({
    queryKey: ["dashboard-tax-year-tyi", taxYear, studentId],
    queryFn: async () => (await api.get(`/dashboard/tax-year/${taxYear}?student_id=${studentId}`)).data,
    enabled: open && !!studentId && !!taxYear,
  });
  const reconStudent = reconDash?.students?.find(s => s.student.id === studentId);
  const recon = reconStudent?.aqee?.recon_1098t;

  // Determine school country for the selected student (shared helper)
  const selectedStudent = students.find(s => s.id === studentId);
  const { isCanada, isForeign } = schoolCountryInfo(selectedStudent);

  const [box1, setBox1] = useState("");
  const [box2, setBox2] = useState("");
  const [box3, setBox3] = useState("");
  const [box3Touched, setBox3Touched] = useState(false); // user has manually edited Box 3
  const [t1Box1, setT1Box1] = useState("");
  const [t1Cur, setT1Cur] = useState("");
  const [t1Box5, setT1Box5] = useState("");
  const [t1Verified, setT1Verified] = useState(false);
  const [notes, setNotes] = useState("");

  React.useEffect(() => {
    setBox1(existing.box1_gross_distribution ?? "");
    setBox2(existing.box2_earnings ?? "");
    setBox3(existing.box3_basis ?? "");
    setBox3Touched(existing.box3_basis != null && existing.box3_basis !== "");
    setT1Box1(existing.form_1098t_box1 ?? "");
    setT1Cur(curCode(existing.form_1098t_box1_currency));
    setT1Box5(existing.form_1098t_box5 ?? "");
    setT1Verified(existing.form_1098t_verified || false);
    setNotes(existing.notes || "");
  }, [existing.box1_gross_distribution, existing.box2_earnings, existing.box3_basis, existing.form_1098t_box1, existing.form_1098t_box1_currency, existing.form_1098t_box5, existing.form_1098t_verified, existing.notes]);

  // ZEN-820: the tuition document figure is typed in the currency printed on
  // the document. Offered only once the server says it compares that way; a
  // figure saved without a currency keeps its US-dollar meaning.
  const docCurrencySupported = reconStudent?.aqee?.tuition_document_currency_supported === true;
  const docCurrency = tuitionDocCurrency({
    supported: docCurrencySupported, isCanada, isForeign, picked: t1Cur,
    schoolCurrency: (selectedStudent?.schools || []).filter(s => !s.archived)[0]?.currency,
    hasStoredFigure: existing.form_1098t_box1 != null && existing.form_1098t_box1 !== "",
  });
  // A currency picked for one beneficiary or year never carries to another.
  React.useEffect(() => { setT1Cur(curCode(existing.form_1098t_box1_currency)); }, [studentId, taxYear, existing.form_1098t_box1_currency]);

  // Auto-suggest Box 3 = Box 1 − Box 2 when both are present and user hasn't touched Box 3
  const suggestedBox3 = (box1 !== "" && box2 !== "") ? Math.max(0, parseFloat(box1) - parseFloat(box2)) : null;
  const effectiveBox3 = box3Touched ? box3 : (suggestedBox3 != null ? String(suggestedBox3) : "");

  // Reconciliation check: Box 1 ≠ Box 2 + Box 3 (with $1 tolerance)
  const b1 = parseFloat(box1), b2 = parseFloat(box2), b3 = parseFloat(effectiveBox3);
  const box3Mismatch = !isNaN(b1) && !isNaN(b2) && !isNaN(b3) && b1 > 0 && Math.abs(b1 - (b2 + b3)) >= 1.0;

  const save = async () => {
    try {
      await api.post("/tax-year-inputs", {
        student_id: studentId, tax_year: parseInt(taxYear),
        box1_gross_distribution: typedFigure(box1), box2_earnings: typedFigure(box2),
        box3_basis: typedFigure(effectiveBox3),
        form_1098t_box1: typedFigure(t1Box1), form_1098t_box5: typedFigure(t1Box5),
        ...(docCurrencySupported ? { form_1098t_box1_currency: typedFigure(t1Box1) != null ? docCurrency : null } : {}),
        form_1098t_verified: t1Verified || null, notes,
      });
      toast.success("Saved"); onSaved();
      qc.invalidateQueries({ queryKey: ["dashboard-tax-year"] });
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tax Year Inputs</DialogTitle><DialogDescription>1099-Q and 1098-T box values for Adjusted Qualified Education Expenses (AQEE) calculation.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <TyiL label="Beneficiary" full><select className="tyiinp" value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="tyi-student"><option value="">Choose a beneficiary…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></TyiL>
          <TyiL label="Tax year"><input type="number" className="tyiinp bg-[#F4F7FB]" value={taxYear} readOnly data-testid="tyi-tax-year" /></TyiL>
        </div>

        {/* 1099-Q */}
        <div className="mt-3 rounded-lg border border-[#C2C9D1] p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">1099-Q (529 Plan Distribution)</h4>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <TyiL label="Box 1 (gross distribution)"><MoneyInput currency="USD" value={box1} onChange={setBox1} placeholder="e.g., 5,300.00" testid="tyi-box1" /></TyiL>
            <TyiL label="Box 2 (earnings)"><MoneyInput currency="USD" value={box2} onChange={setBox2} placeholder="e.g., 950.00" testid="tyi-box2" allowNegative /></TyiL>
            <TyiL label="Box 3 (basis)">
              <MoneyInput currency="USD" value={effectiveBox3} onChange={(v) => { setBox3(v); setBox3Touched(true); }} placeholder={suggestedBox3 != null ? `suggested: ${suggestedBox3}` : "e.g., 4,350.00"} testid="tyi-box3" />
              <span className="mt-0.5 block text-[10px] text-[#9CA3AF]">The original contribution (principal) portion of Box 1.</span>
            </TyiL>
          </div>
          {box3Mismatch && (
            <p className="mt-1 text-[11px] font-semibold text-[#D97706]" data-testid="box3-mismatch">These don&rsquo;t add up &mdash; Box 1 should equal Box 2 + Box 3. Check your form.</p>
          )}
          <p className="mt-1 text-[10px] text-[#9CA3AF]">When distributions exceed AQEE, some earnings become taxable: tax_free = box2 x min(AQEE, box1) / box1.</p>
        </div>

        {/* Tuition statement: 1098-T (US) / T2202 (Canada) / Foreign */}
        {isCanada ? (
          <div className="mt-3 rounded-lg border border-[#C2C9D1] p-3" data-testid="t2202-section">
            <h4 className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">T2202 (Tuition and Enrolment Certificate)</h4>
            <div className="mt-2">
              <TuitionDocAmount label="Box 26 — Total eligible tuition fees" currency={docCurrency} onCurrency={docCurrencySupported ? setT1Cur : null}
                value={t1Box1} onChange={setT1Box1} placeholder="e.g., 8,500.00" testid="tyi-t2202-box26" />
            </div>
            <p className="mt-1 text-[10px] text-[#9CA3AF]">The annual tuition and mandatory fees your school certified for the year.</p>
            <div className="mt-2 rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-2.5 py-1.5 text-[10px] text-[#034AA2]">
              The T2202 reports only tuition. Scholarships, bursaries, and grants aren&rsquo;t on it — they&rsquo;re on a separate T4A slip (Box 105). Record those in <Link to="/scholarships" className="font-semibold underline hover:no-underline">Scholarships</Link>.
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={t1Verified} onChange={(e) => setT1Verified(e.target.checked)} data-testid="tyi-t2202-verified" /> I have verified these values are correct</label>
            <TuitionReconRow recon={recon} taxYear={taxYear} />
          </div>
        ) : isForeign ? (
          <div className="mt-3 rounded-lg border border-[#C2C9D1] p-3" data-testid="foreign-tuition-section">
            <h4 className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Foreign Tuition Statement</h4>
            <div className="mt-2 rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-2.5 py-1.5 text-[10px] text-[#034AA2]">
              Non-US schools generally do not issue a Form 1098-T — enter your tuition from your school&rsquo;s statement (Canadian schools: your T2202) or your own records.
            </div>
            <div className="mt-2">
              <TuitionDocAmount label="Total eligible tuition paid" currency={docCurrency} onCurrency={docCurrencySupported ? setT1Cur : null}
                value={t1Box1} onChange={setT1Box1} placeholder="e.g., 12,000.00" testid="tyi-foreign-tuition" />
            </div>
            <p className="mt-1 text-[10px] text-[#9CA3AF]">Enter what your school charged for tuition and any fees required to enrol or attend (registration, lab, technology, or similar fees everyone must pay). Do not include books, supplies, a computer, or room and board here — those are tracked under Expenses. Use your school&rsquo;s official tuition statement or your own payment records.</p>
            <div className="mt-2 rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-2.5 py-1.5 text-[10px] text-[#034AA2]">
              Scholarships and grants are not entered here. Record those in <Link to="/scholarships" className="font-semibold underline hover:no-underline">Scholarships</Link>.
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={t1Verified} onChange={(e) => setT1Verified(e.target.checked)} data-testid="tyi-foreign-verified" /> I have verified these values are correct</label>
            <TuitionReconRow recon={recon} taxYear={taxYear} />
          </div>
        ) : (
          <div className="mt-3 rounded-lg border border-[#C2C9D1] p-3" data-testid="1098t-section">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">1098-T (School Tuition Statement)</h4>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <TyiL label="Box 1 (payments received)"><MoneyInput currency={docCurrency} value={t1Box1} onChange={setT1Box1} placeholder="e.g., 8,500.00" testid="tyi-1098t-box1" /></TyiL>
              <TyiL label="Box 5 (scholarships/grants)"><MoneyInput currency="USD" value={t1Box5} onChange={setT1Box5} placeholder="e.g., 2,000.00" testid="tyi-1098t-box5" /></TyiL>
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={t1Verified} onChange={(e) => setT1Verified(e.target.checked)} data-testid="tyi-1098t-verified" /> I have verified these 1098-T values are correct</label>
            <p className="mt-1 text-[10px] text-[#9CA3AF]">Enter the box values from your 1098-T, then verify before saving.</p>
            <TuitionReconRow recon={recon} taxYear={taxYear} />
          </div>
        )}

        <NotesField value={notes} onChange={setNotes} testid="tyi-notes" />
        <div className="mt-2 rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-3 py-2 text-[11px] text-[#034AA2]"><b>Estimate — verify with your tax preparer.</b> All computations are aids for record-keeping, not tax advice.</div>
        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} disabled={!studentId} data-testid="tyi-save">Save</Btn></DialogFooter>
        <style>{`.tyiinp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function TyiL({ label, children, full }) { return <label className={`block ${full ? "col-span-2" : ""}`}><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>{children}</label>; }
